import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Site } from 'src/app/models/user';
import { UserService } from 'src/app/services/user.service';
import { PanelData } from 'src/app/settings/settings.constants';
import { IconTypes } from 'src/app/standalones/components/pencil-icon/pencil-icon.component';

import { InstitutionSitesService } from 'src/app/services/institution-sites.service';
import { AclService } from 'src/app/services/acl.service';
import { BehaviorSubject, take } from 'rxjs';
import { IconBackground } from 'src/app/ui/notification-toaster/icon-message-toaster-element/icon-message-toaster-element.component';
import {
  NotificationIconTypes,
  NotificationToasterService,
  NotificationType,
  V2NotificationDataBuilder,
} from 'src/app/services/notification-toaster.service';
import { TranslateService } from '@ngx-translate/core';
import { ToasterPopupStyle } from 'src/app/ui/notification-toaster/custom-notification-toastr/custom-notification-toastr.component';
import { ERRORS, SUCCESSES } from 'src/app/common/utils/notification-constants';

import { CreateAndEditSiteDialogComponent } from './create-and-edit-site-dialog/create-and-edit-site-dialog.component';
@UntilDestroy()
@Component({
  selector: 'app-user-settings-sites-management',
  templateUrl: './user-settings-sites-management.component.html',
  styleUrls: ['./user-settings-sites-management.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserSettingsSitesManagementComponent {
  @Input() panelData!: PanelData;
  private _isLoading$ = new BehaviorSubject(false);
  isLoading$ = this._isLoading$.asObservable();
  protected skeletonBars = Array.from({ length: 5 });
  protected readonly IconTypes = IconTypes;
  sites: Site[] = [];

  constructor(
    private userService: UserService,
    private dialog: MatDialog,
    private institutionSitesService: InstitutionSitesService,
    private translateService: TranslateService,
    private notificationsService: NotificationToasterService,
    private aclService: AclService,
  ) {
    this.subscribeToInstitutionSitesChanges();
  }

  private subscribeToInstitutionSitesChanges(): void {
    this.userService.user.pipe(untilDestroyed(this)).subscribe((res) => {
      this.sites = this.aclService.getInstitutionSites(res?.user.institution) ?? [];
    });
  }

  openAddSiteDialog() {
    const dialogRef = this.dialog.open(CreateAndEditSiteDialogComponent, {
      disableClose: true,
      panelClass: 'zero-padding-dialog',
    });
    dialogRef
      .afterClosed()
      .pipe(untilDestroyed(this))
      .subscribe((result) => {
        if (result) {
          this._isLoading$.next(true);
          this.addSite(result.name);
        }
      });
  }

  private addSite(siteName: string) {
    this.institutionSitesService
      .addSite(siteName)
      .pipe(take(1))
      .subscribe({
        next: (site: Site) => {
          // Successfully Adding site
          if (site) {
            const updatedSites = [...this.sites, site];
            this.updateInstitutionSites(updatedSites);
            this.showToast(
              {
                title: 'Site added successfully',
              },
              false,
            );
            this._isLoading$.next(false);
          }
        },
        error: (error: any) => {
          this.handleError(error, 'add', siteName);
          this._isLoading$.next(false);
        },
      });
  }

  openEditSiteDialog(site: Site) {
    const dialogRef = this.dialog.open(CreateAndEditSiteDialogComponent, {
      data: site,
      disableClose: true,
      panelClass: 'zero-padding-dialog',
    });
    dialogRef
      .afterClosed()
      .pipe(untilDestroyed(this))
      .subscribe((result) => {
        if (result) {
          this._isLoading$.next(true);
          this.updateSite(result);
        }
      });
  }

  private updateSite(siteData: Site) {
    this.institutionSitesService
      .updateSite(siteData._id, siteData.name)
      .pipe(take(1))
      .subscribe({
        next: (updatedSite: Site) => {
          // Successfully updating site
          if (updatedSite) {
            // update site in list
            const updatedSites = this.sites.map((site) =>
              site._id === updatedSite._id ? updatedSite : site,
            );
            this.updateInstitutionSites(updatedSites);
            this.showToast(
              {
                title: 'Site updated successfully',
              },
              false,
            );
            this._isLoading$.next(false);
          }
        },
        error: (error: any) => {
          this.handleError(error, 'rename', siteData.name);
          this._isLoading$.next(false);
        },
      });
  }

  private updateInstitutionSites(updatedSites: Site[]) {
    const currentUser = this.userService.user.getValue();

    if (currentUser) {
      currentUser.user.institution.sites = updatedSites;
      this.userService.emitUpdatedUser(currentUser);
    }
  }

  private handleError(error: any, action: 'add' | 'rename', siteName: string) {
    // Extract error properties
    const backendError = error?.error?.error;
    const backendErrorCode = error?.error?.errorCode;

    // Check and log errors
    if (backendError && backendErrorCode) {
      if (backendErrorCode === 'EXISTING_RECORD') {
        this.showToast(
          {
            title: `Cannot ${action} this site.`,
            description: `"${siteName}" already exists. Please try a different name.`,
          },
          true,
        );
      }
    } else {
      this.showToast(
        {
          title: 'Error',
          description: 'An error happened , please referesh and try again.',
        },
        true,
      );
    }
  }

  private showToast(message?: { title: string; description?: string }, error = false): void {
    if (!message) {
      return;
    }

    const notificationIconConfig = {
      assetName: error ? 'warning_amber_outline' : 'check',
      type: NotificationIconTypes.SVG_ICON,
      width: 18,
      height: 18,
      background: error ? IconBackground.ERROR : IconBackground.SUCCESS,
    };

    const notificationTitle = this.translateService.instant(message.title);
    const notificationType = error ? ERRORS.ACTION_FAILED : SUCCESSES.ACTION_SUCCESSFUL;

    const notificationDescription = message.description
      ? this.translateService.instant(message.description)
      : undefined;

    const v2NotificationDataBuilder = new V2NotificationDataBuilder(notificationType)
      .style(ToasterPopupStyle.WARN)
      .type(NotificationType.WARNING)
      .title(notificationTitle)
      .timeOut(5)
      .showProgressBar(false)
      .toastClass('custom_toast_class')
      .dismissable(true);

    if (notificationIconConfig) {
      v2NotificationDataBuilder.icon(notificationIconConfig);
    }

    if (notificationDescription) {
      v2NotificationDataBuilder.description(notificationDescription);
    }

    const notification = v2NotificationDataBuilder.build();

    this.notificationsService.showNotification(notification);
  }
}
